import {formatISO} from '@shared/helpers/date'
import {isEmpty} from '@shared/helpers/lang'

import {getSaleHistoryDetailByApi, SaleHistoryDetailResponse} from '@web/actions/saleHistory'
import {ClaimStatus, ClaimStatusType} from '@web/core/constants'
import {decodeHtmlEntities} from '@web/helpers/htmlEntities'
import {getInitialState, OrderClaimInfoState} from '@web/slices/orderClaimInfo/slice'
import makeSlice from '@web/slices/orderClaimInfo/sliceMaker'

function findProductOrderLastClaim(productOrders: SaleHistoryDetailResponse['detail']['productOrders']) {
    const inCancelWithAdminNotReject: ClaimStatusType[] = [
        ClaimStatus.CANCEL_REQUEST,
        ClaimStatus.CANCELING,
        ClaimStatus.CANCEL_DONE,
        ClaimStatus.ADMIN_CANCELING,
        ClaimStatus.ADMIN_CANCEL_DONE,
    ]
    const inReturn: ClaimStatusType[] = [
        ClaimStatus.RETURN_REQUEST,
        ClaimStatus.COLLECTING,
        ClaimStatus.COLLECT_DONE,
        ClaimStatus.RETURN_DONE,
        ClaimStatus.RETURN_REJECT,
    ]
    return {
        cancelProductOrder: productOrders.find(({claim}) => {
            return claim && inCancelWithAdminNotReject.includes(claim.claimStatusType)
        }),
        returnProductOrder: productOrders.find(({claim}) => {
            return claim && inReturn.includes(claim.claimStatusType)
        }),
    }
}

type ClaimReasonItems = OrderClaimInfoState['claimReason']['items']

function getPaymentMethodAmountItemsByCancel(
    productOrder: SaleHistoryDetailResponse['detail']['productOrders'][0],
): ClaimReasonItems {
    const claimRequestDateTimeItems: ClaimReasonItems = productOrder.claim?.claimRequestDateTime
        ? [
              {
                  type: 'CANCEL_DATE',
                  amount: 0,
                  value: {
                      date: formatISO(productOrder.claim.claimRequestDateTime),
                  },
              },
          ]
        : []
    const claimRequestAdmissionDateTimeItems: ClaimReasonItems = productOrder.claim?.claimRequestAdmissionDateTime
        ? [
              {
                  type: 'CANCEL_APPLY_DATE',
                  amount: 0,
                  value: {
                      date: formatISO(productOrder.claim.claimRequestAdmissionDateTime),
                  },
              },
          ]
        : []

    const claimRequestReasonItems: ClaimReasonItems = productOrder.claim?.claimRequestReason
        ? [
              {
                  type: 'CANCEL_REASON',
                  amount: 0,
                  value: {
                      text: productOrder.claim.claimRequestReason,
                      detail: decodeHtmlEntities(productOrder.claim.claimRequestDetailContent),
                  },
              },
          ]
        : []

    return [...claimRequestDateTimeItems, ...claimRequestAdmissionDateTimeItems, ...claimRequestReasonItems]
}

function getPaymentMethodAmountItemsByReturn(
    productOrder: SaleHistoryDetailResponse['detail']['productOrders'][0],
): ClaimReasonItems {
    const claimRequestReasonItems: ClaimReasonItems = productOrder.claim?.claimRequestReason
        ? [
              {
                  type: 'RETURN_REASON',
                  amount: 0,
                  value: {
                      text: productOrder.claim.claimRequestReason,
                      detail: decodeHtmlEntities(productOrder.claim.claimRequestDetailContent),
                  },
              },
          ]
        : []
    const dateItems: ClaimReasonItems = productOrder.claim?.claimRequestDateTime
        ? [
              {
                  type: 'RETURN_DATE',
                  amount: 0,
                  value: {
                      date: formatISO(productOrder.claim.claimRequestDateTime),
                  },
              },
          ]
        : []
    const applyItems: ClaimReasonItems = productOrder.claim?.claimRequestAdmissionDateTime
        ? [
              {
                  type: 'RETURN_APPLY_DATE',
                  amount: 0,
                  value: {
                      date: formatISO(productOrder.claim.claimRequestAdmissionDateTime),
                  },
              },
          ]
        : []
    const rejectItems: ClaimReasonItems = productOrder.claim?.claimRejectDateTime
        ? [
              {
                  type: 'RETURN_REJECT_DATE',
                  amount: 0,
                  value: {
                      date: formatISO(productOrder.claim.claimRejectDateTime),
                  },
              },
          ]
        : []
    const amountItems: ClaimReasonItems = productOrder.claim?.extraFeePayAmount
        ? [
              {
                  type: 'RETURN_PRICE',
                  amount: productOrder.claim.extraFeePayAmount,
              },
          ]
        : []

    return [
        // 반품 요청일
        ...dateItems,
        // 반품 승인일
        ...applyItems,
        // 반품 거부일
        ...rejectItems,
        // 반품비용
        ...amountItems,
        // 반품 사유
        ...claimRequestReasonItems,
    ]
}

const orderClaimInfoSlice = makeSlice({
    extraReducers: (builder) => {
        builder
            .addCase(getSaleHistoryDetailByApi.pending, () => {
                return getInitialState()
            })
            .addCase(getSaleHistoryDetailByApi.fulfilled, (state, {payload}) => {
                const {productOrders} = payload.detail
                state.status = 'DISABLE'
                if (payload.refunds && !isEmpty(payload.refunds)) {
                    const {totalRefundAmount, productRefundAmount, refundDeductedAmount} = payload.refunds
                    const {cancelProductOrder, returnProductOrder} = findProductOrderLastClaim(productOrders)

                    if (cancelProductOrder) {
                        state.status = 'ENABLE'
                        state.summary = {
                            type: 'CANCEL',
                            amount: totalRefundAmount,
                        }
                        if (payload.refunds.refundOccurred) {
                            const items: OrderClaimInfoState['claimDetail']['items'] = [
                                {
                                    type: 'REFUND_PRODUCT',
                                    amount: productRefundAmount,
                                },
                                {
                                    type: 'DEDUCT',
                                    amount: refundDeductedAmount,
                                },
                            ]
                            state.claimDetail = {
                                status: 'ENABLE',
                                title: '환불금액',
                                amount: totalRefundAmount,
                                items,
                                types: [],
                            }
                        } else {
                            state.claimDetail.status = 'DISABLE'
                        }

                        state.claimReason = {
                            status: 'ENABLE',
                            items: getPaymentMethodAmountItemsByCancel(cancelProductOrder),
                        }
                    } else if (returnProductOrder) {
                        state.status = 'ENABLE'
                        state.summary = {
                            type: 'RETURN',
                            amount: totalRefundAmount,
                        }
                        if (payload.refunds.refundOccurred) {
                            state.claimDetail = {
                                status: 'ENABLE',
                                title: '환불금액',
                                amount: totalRefundAmount,
                                items: [
                                    {
                                        type: 'REFUND_PRODUCT',
                                        amount: productRefundAmount,
                                    },
                                    {
                                        type: 'DEDUCT',
                                        amount: refundDeductedAmount,
                                    },
                                ],
                                types: [],
                            }
                        } else {
                            state.claimDetail.status = 'DISABLE'
                        }
                        state.claimReason = {
                            status: 'ENABLE',
                            items: getPaymentMethodAmountItemsByReturn(returnProductOrder),
                        }
                    }
                }
            })
    },
})

export default orderClaimInfoSlice.reducer
